import { Euler, Matrix4, Quaternion, Vector3 } from 'three';
import { SBox3 } from '../math/Box3';
import { IAssemblyData } from './assembly/assembly';
import { IFurniture } from './model/furniture';
import { IModelGroupData } from './modelGroup/modelGroup';
import { BoundingBox3 } from '../math/BoundingBox3';
import { EEntityType, IRotatedBoxPure, ISelected } from '../types';
import { globalStores } from '../stores';
import { getMultiModelBoxes } from './multiModel/utils';
import { IModelInfo, IMultiModelData } from './multiModel/multiModel';
import { EColumnType, IColumnData, IWindowData } from './model/model';
import { isMultiSelectDoorWindowOnly } from '../utils/select/multi-select';
import { Point2dUtils } from '../math/Point2dUtils';

export function getModelBoundingBox(data: IFurniture | IAssemblyData | IModelGroupData) {
  const { width, thickness, height, offGround, center, rotation } = data;
  const box3 = new SBox3(
    new Vector3(width, thickness, height),
    new Vector3(center.x, center.y, offGround + height / 2),
    new Vector3(0, 0, rotation)
  );

  return box3;
}

export function getModelsBoundingBox(datas: (IFurniture | IAssemblyData)[]) {
  const assemblyBoundingBox = new BoundingBox3();
  for (const data of datas) {
    const box = getModelBoundingBox(data);
    const points = box.points;
    assemblyBoundingBox.includePoints(points);
  }

  return assemblyBoundingBox;
}

export interface IGetPureBoxesResult {
  x: number;
  y: number;
  z: number;
  rotation: number;
  boxes: IRotatedBoxPure[];
}
// 获取对应的盒子数据，boxes数组按子到父排列，根节点在最后
export function getPureBoxes(selected: ISelected): IGetPureBoxesResult | undefined {
  const data = globalStores.getDataByIdType(selected.entityId, selected.type);
  return getPureBoxesByData(selected.type, data);
}

export function getPureBoxesByData(entityType: EEntityType, data: unknown): IGetPureBoxesResult | undefined {
  if (entityType === EEntityType.EMPTY) return;
  if (!data) return;

  switch (entityType) {
    case EEntityType.Furniture: {
      const modelData = data as IFurniture;

      return {
        x: modelData.center.x,
        y: modelData.center.y,
        z: modelData.offGround + modelData.height / 2,
        rotation: modelData.rotation || 0,
        boxes: [
          {
            width: modelData.width,
            thickness: modelData.thickness,
            height: modelData.height,
            x: 0,
            y: 0,
            z: 0,
            rotation: 0
          }
        ]
      };
    }
    case EEntityType.DoorWindow: {
      const windowData = data as IWindowData;
      return {
        x: windowData.center.x,
        y: windowData.center.y,
        z: windowData.offGround + windowData.height / 2,
        rotation: windowData.rotation || 0,
        boxes: [
          {
            width: windowData.width,
            thickness: windowData.thickness,
            height: windowData.height,
            x: 0,
            y: 0,
            z: 0,
            rotation: 0
          }
        ]
      };
    }
    case EEntityType.Column: {
      const columnData = data as IColumnData;

      let width;
      let thickness;

      if (columnData.type === EColumnType.cylinder) {
        width = columnData.radius * 2;
        thickness = columnData.radius * 2;
      } else {
        width = columnData.width;
        thickness = columnData.thickness;
      }

      return {
        x: columnData.center.x,
        y: columnData.center.y,
        z: columnData.offGround + columnData.height / 2,
        rotation: columnData.rotation,
        boxes: [{ width, thickness, height: columnData.height, x: 0, y: 0, z: 0, rotation: 0 }]
      };
    }
    case EEntityType.MultiModel: {
      const modelData = data as IMultiModelData;

      // 门窗多选只画各自的盒，不套外层容器盒
      if (isMultiSelectDoorWindowOnly(modelData)) {
        const doorWindowsData = modelData.modelInfos.map((info) => info.data);
        return {
          x: modelData.center.x,
          y: modelData.center.y,
          z: modelData.offGround + modelData.height / 2,
          rotation: modelData.rotation || 0,
          boxes: doorWindowsData.map((windowData) => ({
            width: windowData.width,
            thickness: windowData.thickness,
            height: windowData.height,
            x: Point2dUtils.subtracted(windowData.center, modelData.center).x,
            y: Point2dUtils.subtracted(windowData.center, modelData.center).y,
            z: windowData.offGround + windowData.height / 2 - (modelData.offGround + modelData.height / 2),
            rotation: windowData.rotation
          }))
        };
      }

      // 家具、组合、隔墙及混合选中，子盒由 getModelInfoBox 统一归一，隔墙走轮廓推导
      return getContainerPureBoxes(modelData.modelInfos, {
        x: modelData.center.x,
        y: modelData.center.y,
        z: modelData.offGround + modelData.height / 2,
        rotation: modelData.rotation,
        width: modelData.width,
        thickness: modelData.thickness,
        height: modelData.height
      });
    }
    case EEntityType.Assembly: {
      const modelData = data as IAssemblyData;
      const modelInfos: IModelInfo[] = [];
      for (const accessorieId of modelData.accessorieIds) {
        const furnitureData = globalStores.modelStore.getFurnitureData(accessorieId);
        // 取不到的配件直接跳过，否则 data 为 undefined 会在包围盒计算里炸掉
        if (!furnitureData) continue;
        modelInfos.push({
          id: accessorieId,
          type: EEntityType.Furniture,
          data: furnitureData,
          subModeldatas: []
        });
      }

      return getContainerPureBoxes(modelInfos, {
        x: modelData.center.x,
        y: modelData.center.y,
        z: modelData.offGround + modelData.height / 2,
        rotation: modelData.rotation,
        width: modelData.width,
        thickness: modelData.thickness,
        height: modelData.height
      });
    }
  }
}

/**
 * 由子盒 + 容器盒装配出选中框数据，boxes 按子到父排列。
 * 只有一个子盒时不再叠加容器盒，否则会在同一位置画两层框。
 *
 * 隔墙不出子盒：它的盒是轴对齐的，斜墙/弧墙会画出明显大于墙体的框，
 * 改由 SelectedHoveredManager 把墙体本身置为选中态高亮。容器盒仍然包含隔墙的范围。
 */
function getContainerPureBoxes(modelInfos: IModelInfo[], container: IRotatedBoxPure): IGetPureBoxesResult | undefined {
  const boxModelInfos = modelInfos.filter((modelInfo) => modelInfo.type !== EEntityType.SeparateWall);
  const sepWallCount = modelInfos.length - boxModelInfos.length;

  const boxesObj = getMultiModelBoxes({ modelInfos: boxModelInfos, skipBoundingBoxCalculate: true });
  const children = boxesObj.children; // getMultiModelBoxes 已把组合的子盒摊平
  if (children.length + sepWallCount === 0) return;
  // 只有一面隔墙时，容器盒就是这面墙的轴对齐盒，画出来正是要避免的东西，只留墙体高亮
  if (children.length === 0 && sepWallCount === 1) return;

  const resultBoxes: IRotatedBoxPure[] = [...children];
  if (children.length + sepWallCount > 1) resultBoxes.push(container);

  const containerCoordinate = {
    x: container.x,
    y: container.y,
    z: container.z,
    rotation: container.rotation
  };

  return {
    ...containerCoordinate,
    boxes: transformToContainerCoordinate(resultBoxes, containerCoordinate)
  };
}

function transformToContainerCoordinate(
  boxes: IRotatedBoxPure[],
  container: Pick<IRotatedBoxPure, 'x' | 'y' | 'z' | 'rotation'>
): IRotatedBoxPure[] {
  const containerInvertMatrix = new Matrix4()
    .compose(
      new Vector3(container.x, container.y, container.z),
      new Quaternion().setFromEuler(new Euler(0, 0, container.rotation)),
      new Vector3(1, 1, 1)
    )
    .invert();

  const position = new Vector3();
  const quaternion = new Quaternion();
  const euler = new Euler();
  const scale = new Vector3();
  return boxes.map((box) => {
    position.set(box.x, box.y, box.z);
    euler.set(0, 0, box.rotation);
    quaternion.setFromEuler(euler);
    scale.set(1, 1, 1);

    const boxMatrix = new Matrix4().compose(position, quaternion, scale);
    const localMatrix = containerInvertMatrix.clone().multiply(boxMatrix);
    localMatrix.decompose(position, quaternion, scale);
    euler.setFromQuaternion(quaternion);

    return {
      ...box,
      x: position.x,
      y: position.y,
      z: position.z,
      rotation: euler.z
    };
  });
}
